import "./productDetail.css";

import React, { useEffect, useState } from "react";
import TitleBox from "../../../components/TitleBox/TitleBox";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { productsDetail } from "../../../assets/images/productsDetail";
import { ModalContextValue } from "../../../context/ModalContext";
import NullDataComponent from "../../../components/NullDataComponent/NullDataComponent";
import ProductGalleryLightbox from "../../../components/ProductGalleryLightbox/ProductGalleryLightbox";

function getProductTitle(productName, t) {
  const titleMap = {
    OTOBÜS: t("products_bus"),
    "MİNİBÜS": t("products_minibus"),
    KAMYON: t("products_truck"),
    HAVACILIK: t("products_aviation"),
    "TRAKTÖR": t("products_tractor"),
    "ELEKTRİKLİ ARAÇ": t("products_electric_vehicle"),
    "SAVUNMA SANAYİ ARAÇLARI": t("products_defense_industry_tools"),
    OTOMOBİL: t("products_car"),
  };

  return titleMap[productName] || "";
}

function getBrandDisplayParts(brand) {
  if (brand?.displayParts?.length) {
    return brand.displayParts;
  }

  return [
    {
      label: brand?.tagLabel || brand?.name || "",
      model: "",
    },
  ];
}

function getBrandPlainText(brand) {
  return getBrandDisplayParts(brand)
    .map((part) => [part.label, part.model].filter(Boolean).join(" "))
    .join(" / ");
}

function BrandDisplay({ brand, variant = "inline" }) {
  const parts = getBrandDisplayParts(brand);

  return (
    <span className={`detail-brand-display detail-brand-display-${variant}`}>
      {parts.map((part, index) => (
        <React.Fragment key={`${part.label}-${part.model}-${index}`}>
          <span className="detail-brand-token">
            <span className="detail-brand-name">{part.label}</span>
            {part.model ? (
              <span className="detail-model-accent">{part.model}</span>
            ) : null}
          </span>
          {variant === "stack" || index === parts.length - 1 ? null : (
            <span className="detail-brand-divider">/</span>
          )}
        </React.Fragment>
      ))}
    </span>
  );
}

function ProductDetail() {
  const { t, i18n } = useTranslation();
  const { productId } = useParams();
  const { openModal, setOpenModal } = ModalContextValue();

  const [loading, setLoading] = useState(false);
  const [productName, setProductName] = useState("");
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectDataIndex, setSelectDataIndex] = useState(0);

  useEffect(() => {
    const product = productsDetail.find(
      (prd) => Number(prd.productId) === Number(productId)
    );

    if (!product) {
      setBrands([]);
      setSelectedBrand(null);
      setProductName("");
      return;
    }

    setBrands(product.brands);
    setSelectedBrand(product.brands[0] || null);
    setProductName(getProductTitle(product.productName, t));
  }, [productId, i18n.language, t]);

  useEffect(() => {
    if (!selectedBrand) {
      return undefined;
    }

    setLoading(true);
    const timer = window.setTimeout(() => {
      setLoading(false);
    }, 220);

    return () => window.clearTimeout(timer);
  }, [selectedBrand]);

  useEffect(() => {
    return () => {
      setOpenModal(false);
    };
  }, [setOpenModal]);

  function handleOpenModal(index) {
    setSelectDataIndex(index);
    setOpenModal(true);
  }

  function handleCloseModal() {
    setOpenModal(false);
  }

  const selectedBrandLabel = selectedBrand?.tagLabel || selectedBrand?.name || "";
  const selectedBrandPlainText = getBrandPlainText(selectedBrand);
  const selectedBrandImageCount = selectedBrand?.src?.length || 0;

  const capabilityItems = [
    t("product_detail_capability_thermoforming"),
    t("product_detail_capability_extrusion"),
    t("product_detail_capability_cnc"),
    t("product_detail_capability_quality"),
  ];

  const metricItems = [
    {
      value: "1994",
      label: t("product_detail_metric_founded"),
    },
    {
      value: "17.000 m²",
      label: t("product_detail_metric_area"),
    },
    {
      value: "50+",
      label: t("product_detail_metric_export"),
    },
    {
      value: t("product_detail_metric_process_value"),
      label: t("product_detail_metric_process"),
    },
  ];

  return (
    <div className="front-container-product-detail">
      <div className="header-title">
        <TitleBox button={true} title={`${t("products")} / ${productName}`} />
      </div>

      {loading ? (
        <div className="detail-loading">{t("loading")}</div>
      ) : brands && brands.length > 0 ? (
        <div className="detail-shell">
          <section className="detail-overview-card">
            <div className="detail-overview-copy">
              <span className="detail-kicker">
                {t("product_detail_intro_eyebrow")}
              </span>
              <h1 className="detail-overview-title">{selectedBrandLabel}</h1>
              <div className="detail-model-line">
                <span className="detail-model-label">
                  {t("product_detail_model_scope_label")}
                </span>
                <BrandDisplay brand={selectedBrand} />
              </div>
              <p className="detail-overview-lead">
                {t("product_detail_intro_lead", {
                  productName,
                })}
              </p>
              <p className="detail-overview-text">
                {t("product_detail_intro_text1")}
              </p>
              <p className="detail-overview-text">
                {t("product_detail_intro_text2")}
              </p>

              <div className="detail-capability-list">
                {capabilityItems.map((item) => (
                  <span key={item} className="detail-capability-pill">
                    {item}
                  </span>
                ))}
              </div>
            </div>

            <aside className="detail-overview-panel">
              <span className="detail-kicker">
                {t("product_detail_panel_eyebrow")}
              </span>
              <h2 className="detail-panel-title">
                {t("product_detail_panel_title")}
              </h2>
              <p className="detail-panel-copy">
                {t("product_detail_panel_text1")}
              </p>
              <p className="detail-panel-copy">
                <span className="detail-panel-copy-strong">
                  <BrandDisplay brand={selectedBrand} />
                </span>{" "}
                {t("product_detail_panel_text2", {
                  count: selectedBrandImageCount,
                })}
              </p>

              <div className="detail-stats-grid">
                {metricItems.map((item) => (
                  <div key={item.label} className="detail-stat-card">
                    <span className="detail-stat-value">{item.value}</span>
                    <span className="detail-stat-label">{item.label}</span>
                  </div>
                ))}
              </div>
            </aside>
          </section>

          <section className="detail-filter-card">
            <div className="detail-filter-header">
              <div>
                <span className="detail-kicker">
                  {t("product_detail_filter_eyebrow")}
                </span>
                <h2 className="detail-section-title">
                  {t("product_detail_filter_title")}
                </h2>
                <p className="detail-section-text">
                  {t("product_detail_filter_hint")}
                </p>
              </div>
              <span className="detail-filter-badge">
                {t("product_detail_filter_count", { count: brands.length })}
              </span>
            </div>

            <div className="detail-links">
              {brands.map((brand, index) => (
                <button
                  key={`${brand.name}-${index}`}
                  type="button"
                  className={`link-button ${
                    selectedBrand && selectedBrand.name === brand.name
                      ? "active"
                      : ""
                  }`}
                  onClick={() => setSelectedBrand(brand)}
                >
                  <span className="link-button-title">{brand.tagLabel || brand.name}</span>
                  <span className="link-button-spec">
                    <BrandDisplay brand={brand} variant="stack" />
                  </span>
                  <span className="link-button-meta">
                    {t("product_detail_filter_visual_count", {
                      count: brand.src.length,
                    })}
                  </span>
                </button>
              ))}
            </div>
          </section>

          <section className="detail-gallery-card">
            <div className="detail-gallery-header">
              <div>
                <span className="detail-kicker">
                  {t("product_detail_gallery_eyebrow")}
                </span>
                <h2 className="detail-section-title">{selectedBrandLabel}</h2>
                <div className="detail-gallery-models">
                  <BrandDisplay brand={selectedBrand} />
                </div>
              </div>
              <p className="detail-section-text">
                {t("product_detail_gallery_hint")}
              </p>
            </div>

            <div className="detail-images">
              {selectedBrand &&
                selectedBrand.src.map((image, index) => (
                  <button
                    key={`${selectedBrand.name}-${index}`}
                    type="button"
                    className="image-box"
                    onClick={() => handleOpenModal(index)}
                  >
                    <img
                      src={image}
                      alt={`${selectedBrandPlainText} ${index + 1}`}
                      className="brand-image"
                    />
                    <div className="image-overlay">
                      <span className="image-index">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="image-action">
                        {t("product_detail_gallery_open")}
                      </span>
                    </div>
                  </button>
                ))}
            </div>
          </section>

          <ProductGalleryLightbox
            brand={selectedBrand}
            index={selectDataIndex}
            open={openModal && Boolean(selectedBrand)}
            onClose={handleCloseModal}
          />
        </div>
      ) : (
        <NullDataComponent />
      )}
    </div>
  );
}

export default ProductDetail;
